// Progress Tracking Utilities
// Manages user progress, streaks, and statistics using localStorage
// When user is signed in, also saves to Firestore via cloudSync.

import { saveToCloud, saveVisionToCloud, currentUser } from './cloudSync';

const STORAGE_KEY = 'eyeTrainer_progress';

export const ProgressTracking = {
    // Initialize or get existing progress data
    getProgress() {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
        return this.initializeProgress();
    },

    initializeProgress() {
        const initialData = {
            completedDays: {}, // { "2026-01-14": { count: 1, difficulty: "intermediate", points: 50 } }
            currentStreak: 0,
            longestStreak: 0,
            totalSessions: 0,
            totalPoints: 0,
            badges: [],
            lastCompletedDate: null,
            firstSessionDate: null,
            difficultyHistory: {}, // { "beginner": 3, "advanced": 5, ... }
        };
        this.saveProgress(initialData);
        return initialData;
    },

    saveProgress(data) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        // If signed in, also persist to Firestore (fire-and-forget)
        if (currentUser()) {
            saveToCloud(data).catch(err => console.warn('Cloud save failed:', err));
        }
    },

    // Record a completed exercise session
    recordSession(difficulty) {
        const progress = this.getProgress();
        const today = this.getTodayDate();

        // Ensure difficultyHistory exists (backward compat)
        if (!progress.difficultyHistory) {
            progress.difficultyHistory = {};
        }

        // Track days since last session (for comeback badge)
        const previousLastDate = progress.lastCompletedDate;

        // Initialize first session date
        if (!progress.firstSessionDate) {
            progress.firstSessionDate = today;
        }

        // Calculate points based on difficulty
        const points = this.calculatePoints(difficulty);

        // Update or create today's entry
        if (!progress.completedDays[today]) {
            progress.completedDays[today] = {
                count: 1,
                difficulty: difficulty,
                points: points,
                timestamp: new Date().toISOString()
            };
        } else {
            progress.completedDays[today].count += 1;
            progress.completedDays[today].points += points;
        }

        // Update totals
        progress.totalSessions += 1;
        progress.totalPoints += points;
        progress.lastCompletedDate = today;

        // Update difficulty history
        progress.difficultyHistory[difficulty] = (progress.difficultyHistory[difficulty] || 0) + 1;

        // Update streaks
        this.updateStreaks(progress);

        // Check for new badges
        this.checkAndAwardBadges(progress, previousLastDate);

        this.saveProgress(progress);
        return progress;
    },

    calculatePoints(difficulty) {
        const pointsMap = {
            'beginner': 10,
            'intermediate': 25,
            'advanced': 50,
            'intense': 100,
            'progressiveEasy': 15,
            'progressiveMedium': 35,
            'progressiveTough': 75,
            'geometricPatterns': 40,
            'focusCatcher': 30,
        };
        return pointsMap[difficulty] || 10;
    },

    updateStreaks(progress) {
        const today = this.getTodayDate();
        const yesterday = this.getDateDaysAgo(1);

        // If completed today or yesterday, continue or start streak
        if (progress.completedDays[yesterday] || progress.completedDays[today]) {
            // Calculate current streak by counting backwards
            let streak = 0;
            let checkDate = today;

            while (progress.completedDays[checkDate]) {
                streak++;
                checkDate = this.getDateDaysAgo(streak);
            }

            progress.currentStreak = streak;

            // Update longest streak
            if (progress.currentStreak > progress.longestStreak) {
                progress.longestStreak = progress.currentStreak;
            }
        } else {
            // Streak broken
            progress.currentStreak = 1;
        }
    },

    checkAndAwardBadges(progress, previousLastDate) {
        const badges = [];
        const now = new Date().toISOString();
        const today = this.getTodayDate();
        const dh = progress.difficultyHistory || {};

        const award = (id, name, icon) => {
            if (!this.hasBadge(progress, id)) {
                badges.push({ id, name, icon, date: now });
            }
        };

        // ── Milestone ──
        if (progress.totalSessions >= 1) award('first_session', 'First Steps', '🎯');

        // ── Streak badges ──
        if (progress.currentStreak >= 3)   award('3_day_streak', '3-Day Streak', '🔥');
        if (progress.currentStreak >= 7)   award('7_day_streak', 'Week Warrior', '⭐');
        if (progress.currentStreak >= 14)  award('14_day_streak', 'Fortnight Focus', '🏅');
        if (progress.currentStreak >= 30)  award('30_day_streak', 'Monthly Master', '👑');
        if (progress.currentStreak >= 60)  award('60_day_streak', 'Diamond Discipline', '💠');
        if (progress.currentStreak >= 365) award('365_day_streak', 'Year of Vision', '🌍');

        // ── Session count badges ──
        if (progress.totalSessions >= 10)  award('10_sessions', 'Dedicated', '💪');
        if (progress.totalSessions >= 25)  award('25_sessions', 'Quarter Century', '🎖️');
        if (progress.totalSessions >= 50)  award('50_sessions', 'Eye Champion', '🏆');
        if (progress.totalSessions >= 100) award('100_sessions', 'Vision Legend', '💎');
        if (progress.totalSessions >= 200) award('200_sessions', 'Bicentennial', '⚡');

        // ── Points badges ──
        if (progress.totalPoints >= 100)  award('100_points', 'Century Score', '💯');
        if (progress.totalPoints >= 1000) award('1000_points', 'Point Master', '🔮');
        if (progress.totalPoints >= 5000) award('5000_points', 'Elite Scorer', '✨');

        // ── Difficulty & variety badges ──
        const allDifficulties = ['beginner', 'intermediate', 'advanced', 'intense', 'progressiveEasy', 'progressiveMedium', 'progressiveTough', 'geometricPatterns'];
        const usedCount = allDifficulties.filter(d => dh[d] > 0).length;
        if (usedCount >= allDifficulties.length) award('explorer', 'Explorer', '🧭');

        const hardSessions = (dh['advanced'] || 0) + (dh['intense'] || 0) + (dh['progressiveTough'] || 0);
        if (hardSessions >= 5) award('iron_eyes', 'Iron Eyes', '🦾');

        if ((dh['geometricPatterns'] || 0) >= 10) award('pattern_pro', 'Pattern Pro', '🔷');

        if (dh['progressiveEasy'] > 0 && dh['progressiveMedium'] > 0 && dh['progressiveTough'] > 0) {
            award('level_up', 'Level Up', '📈');
        }

        // ── Multi-session day badges ──
        const todayData = progress.completedDays[today];
        if (todayData && todayData.count >= 2) award('overachiever', 'Overachiever', '✌️');
        if (todayData && todayData.count >= 3) award('hat_trick', 'Hat Trick', '🎩');

        // ── Re-engagement badges ──
        if (previousLastDate && previousLastDate !== today) {
            const dayGap = this.daysBetween(previousLastDate, today);
            if (dayGap >= 7) award('comeback_kid', 'Comeback Kid', '🔄');
        }

        // Weekend warrior: both Saturday and Sunday of the same weekend
        const todayDow = new Date().getDay(); // 0=Sun, 6=Sat
        if (todayDow === 0 || todayDow === 6) {
            let saturday, sunday;
            if (todayDow === 6) {
                saturday = today;
                const sunDate = new Date();
                sunDate.setDate(sunDate.getDate() + 1);
                sunday = this.formatDate(sunDate);
            } else {
                sunday = today;
                const satDate = new Date();
                satDate.setDate(satDate.getDate() - 1);
                saturday = this.formatDate(satDate);
            }
            if (progress.completedDays[saturday] && progress.completedDays[sunday]) {
                award('weekend_warrior', 'Weekend Warrior', '🌅');
            }
        }

        // Add new badges to progress
        if (badges.length > 0) {
            progress.badges = [...progress.badges, ...badges];
        }

        return badges;
    },

    // Check and award vision-test-related badges
    checkAndAwardVisionBadges() {
        const progress = this.getProgress();
        const tests = this.getVisionTests();
        const badges = [];
        const now = new Date().toISOString();

        const award = (id, name, icon) => {
            if (!this.hasBadge(progress, id)) {
                badges.push({ id, name, icon, date: now });
            }
        };

        if (tests.length >= 1)  award('eye_curious', 'Eye Curious', '🔍');
        if (tests.length >= 5)  award('progress_watcher', 'Progress Watcher', '📊');

        // 20/20 Club: decimal >= 1.0 means 20/20 or better
        const hasPerfect = tests.some(t => t.decimal >= 1.0);
        if (hasPerfect) award('perfect_vision', '20/20 Club', '👁️');

        if (badges.length > 0) {
            progress.badges = [...progress.badges, ...badges];
            this.saveProgress(progress);
        }

        return badges;
    },

    // Calculate the number of days between two date strings
    daysBetween(dateStr1, dateStr2) {
        const d1 = new Date(dateStr1);
        const d2 = new Date(dateStr2);
        return Math.round(Math.abs((d2 - d1) / (1000 * 60 * 60 * 24)));
    },

    hasBadge(progress, badgeId) {
        return progress.badges.some(badge => badge.id === badgeId);
    },

    // Get calendar data for visualization (last 90 days)
    getCalendarData(days = 90) {
        const progress = this.getProgress();
        const calendar = [];

        for (let i = days - 1; i >= 0; i--) {
            const date = this.getDateDaysAgo(i);
            const dayData = progress.completedDays[date];

            calendar.push({
                date: date,
                completed: !!dayData,
                count: dayData ? dayData.count : 0,
                points: dayData ? dayData.points : 0,
                level: this.getActivityLevel(dayData)
            });
        }

        return calendar;
    },

    getActivityLevel(dayData) {
        if (!dayData) return 0;
        if (dayData.count >= 3) return 4;
        if (dayData.count >= 2) return 3;
        if (dayData.count >= 1) return 2;
        return 1;
    },

    // Get statistics summary
    getStats() {
        const progress = this.getProgress();
        const calendar = this.getCalendarData(90);
        const completedDaysCount = Object.keys(progress.completedDays).length;

        return {
            currentStreak: progress.currentStreak,
            longestStreak: progress.longestStreak,
            totalSessions: progress.totalSessions,
            totalPoints: progress.totalPoints,
            completedDays: completedDaysCount,
            badges: progress.badges,
            lastCompletedDate: progress.lastCompletedDate,
            firstSessionDate: progress.firstSessionDate,
            difficultyHistory: progress.difficultyHistory || {},
            calendar: calendar
        };
    },

    // Utility functions
    getTodayDate() {
        const now = new Date();
        return this.formatDate(now);
    },

    getDateDaysAgo(days) {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return this.formatDate(date);
    },

    formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },

    // Get day of week (0 = Sunday, 6 = Saturday)
    getDayOfWeek(dateString) {
        return new Date(dateString).getDay();
    },

    // ============ Vision Test Results ============

    /**
     * Record a vision self-assessment result.
     * @param {{eye: 'left'|'right'|'both', logMAR: number, snellenFt: string, decimal: number, description: string}} result
     */
    recordVisionTest(result) {
        const VISION_KEY = 'eyeTrainer_visionTests';
        const stored = localStorage.getItem(VISION_KEY);
        const history = stored ? JSON.parse(stored) : [];

        history.push({
            ...result,
            date: new Date().toISOString(),
        });

        // Keep the last 100 results to avoid unbounded growth
        if (history.length > 100) {
            history.splice(0, history.length - 100);
        }

        localStorage.setItem(VISION_KEY, JSON.stringify(history));

        // If signed in, persist vision tests to cloud
        if (currentUser()) {
            saveVisionToCloud(history).catch(err => console.warn('Cloud vision save failed:', err));
        }

        // Check for vision-related badges
        this.checkAndAwardVisionBadges();

        return history;
    },

    /**
     * Get all stored vision test results.
     * @returns {Array}
     */
    getVisionTests() {
        const stored = localStorage.getItem('eyeTrainer_visionTests');
        return stored ? JSON.parse(stored) : [];
    },

    /**
     * Get the most recent test result for each eye.
     * @returns {{left: object|null, right: object|null, both: object|null}}
     */
    getLatestVisionResults() {
        const tests = this.getVisionTests();
        const latest = { left: null, right: null, both: null };
        // Walk backwards to find the most recent for each eye
        for (let i = tests.length - 1; i >= 0; i--) {
            const t = tests[i];
            if (!latest[t.eye]) {
                latest[t.eye] = t;
            }
            if (latest.left && latest.right && latest.both) break;
        }
        return latest;
    },

    // Get session data for the current week (Mon–Sun)
    getWeekData() {
        const progress = this.getProgress();
        const today = new Date();
        const todayStr = this.getTodayDate();
        // Get Monday of current week
        const dayOfWeek = today.getDay(); // 0=Sun
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const monday = new Date(today);
        monday.setDate(today.getDate() - mondayOffset);

        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            const dateStr = this.formatDate(d);
            const dayData = progress.completedDays[dateStr];
            days.push({
                date: dateStr,
                dayLabel: d.toLocaleDateString('en', { weekday: 'short' }).substring(0, 2),
                count: dayData ? dayData.count : 0,
                points: dayData ? dayData.points : 0,
                isToday: dateStr === todayStr,
                isFuture: dateStr > todayStr,
            });
        }
        return days;
    },

    // All possible badges with their unlock conditions
    getAllBadges() {
        return [
            { id: 'first_session', icon: '🎯', req: 'totalSessions', val: 1 },
            { id: '3_day_streak', icon: '🔥', req: 'currentStreak', val: 3 },
            { id: '7_day_streak', icon: '⭐', req: 'currentStreak', val: 7 },
            { id: '14_day_streak', icon: '🏅', req: 'currentStreak', val: 14 },
            { id: '30_day_streak', icon: '👑', req: 'currentStreak', val: 30 },
            { id: '60_day_streak', icon: '💠', req: 'currentStreak', val: 60 },
            { id: '365_day_streak', icon: '🌍', req: 'currentStreak', val: 365 },
            { id: '10_sessions', icon: '💪', req: 'totalSessions', val: 10 },
            { id: '25_sessions', icon: '🎖️', req: 'totalSessions', val: 25 },
            { id: '50_sessions', icon: '🏆', req: 'totalSessions', val: 50 },
            { id: '100_sessions', icon: '💎', req: 'totalSessions', val: 100 },
            { id: '200_sessions', icon: '⚡', req: 'totalSessions', val: 200 },
            { id: '100_points', icon: '💯', req: 'totalPoints', val: 100 },
            { id: '1000_points', icon: '🔮', req: 'totalPoints', val: 1000 },
            { id: '5000_points', icon: '✨', req: 'totalPoints', val: 5000 },
            { id: 'explorer', icon: '🧭', req: 'special', val: 0 },
            { id: 'iron_eyes', icon: '🦾', req: 'special', val: 0 },
            { id: 'pattern_pro', icon: '🔷', req: 'special', val: 0 },
            { id: 'level_up', icon: '📈', req: 'special', val: 0 },
            { id: 'overachiever', icon: '✌️', req: 'special', val: 0 },
            { id: 'hat_trick', icon: '🎩', req: 'special', val: 0 },
            { id: 'comeback_kid', icon: '🔄', req: 'special', val: 0 },
            { id: 'weekend_warrior', icon: '🌅', req: 'special', val: 0 },
            { id: 'eye_curious', icon: '🔍', req: 'special', val: 0 },
            { id: 'progress_watcher', icon: '📊', req: 'special', val: 0 },
            { id: 'perfect_vision', icon: '👁️', req: 'special', val: 0 },
        ];
    },

    // Get the next achievable goal for the user
    getNextGoal() {
        const progress = this.getProgress();
        const goals = [];

        // Streak goals
        const streakMilestones = [3, 7, 14, 30, 60, 365];
        for (const m of streakMilestones) {
            if (progress.currentStreak < m) {
                goals.push({
                    type: 'streak',
                    target: m,
                    current: progress.currentStreak,
                    remaining: m - progress.currentStreak,
                    progress: progress.currentStreak / m,
                });
                break;
            }
        }

        // Session goals
        const sessionMilestones = [1, 10, 25, 50, 100, 200];
        for (const m of sessionMilestones) {
            if (progress.totalSessions < m) {
                goals.push({
                    type: 'sessions',
                    target: m,
                    current: progress.totalSessions,
                    remaining: m - progress.totalSessions,
                    progress: progress.totalSessions / m,
                });
                break;
            }
        }

        // Points goals
        const pointsMilestones = [100, 1000, 5000];
        for (const m of pointsMilestones) {
            if (progress.totalPoints < m) {
                goals.push({
                    type: 'points',
                    target: m,
                    current: progress.totalPoints,
                    remaining: m - progress.totalPoints,
                    progress: progress.totalPoints / m,
                });
                break;
            }
        }

        // Return the goal with highest progress (closest to completion)
        if (goals.length === 0) return null;
        goals.sort((a, b) => b.progress - a.progress);
        return goals[0];
    },

    // Reset all progress (for testing or user request)
    resetProgress() {
        localStorage.removeItem(STORAGE_KEY);
        return this.initializeProgress();
    }
};

export default ProgressTracking;
